import React from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../shared/CurrencyContext';
import PageSection from '../../../components/layout/PageSection';
import StatsCard from '../../../components/ui/StatsCard';
import EmptyState from '../../../components/feedback/EmptyState';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement
);

const Statistics = ({ rental, rentalItems }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();

  if (!rental || !rentalItems) {
    return (
      <PageSection>
        <Alert variant="danger" className="app-inline-alert mb-0">{t('statistics.errorFetching')}</Alert>
      </PageSection>
    );
  }

  // Calculate rental duration
  const dateFrom = new Date(rental.date_from);
  const dateTo = new Date(rental.date_to);
  const durationInDays = Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60 * 24));
  const durationInHours = Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60));

  // Process rental items statistics
  const itemsByCategory = rentalItems.reduce((acc, item) => {
    const category = item.stock_item_product_category_name || 'Other';
    if (!acc[category]) {
      acc[category] = { count: 0, totalQuantity: 0 };
    }
    acc[category].count++;
    acc[category].totalQuantity += item.quantity;
    return acc;
  }, {});

  const itemsByType = rentalItems.reduce((acc, item) => {
    const type = item.stock_item_product_type_name || 'Other';
    if (!acc[type]) {
      acc[type] = { count: 0, totalQuantity: 0 };
    }
    acc[type].count++;
    acc[type].totalQuantity += item.quantity;
    return acc;
  }, {});

  // Calculate total values
  const totalItems = rentalItems.length;
  const totalQuantity = rentalItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = rental.total_price || 0;
  const averagePricePerItem = totalItems > 0 ? totalPrice / totalItems : 0;
  const averagePricePerDay = durationInDays > 0 ? totalPrice / durationInDays : 0;

  // Chart data for items by category
  const categoryChartData = {
    labels: Object.keys(itemsByCategory),
    datasets: [
      {
        data: Object.values(itemsByCategory).map(cat => cat.totalQuantity),
        backgroundColor: [
          '#198754', '#0d6efd', '#ffc107', '#dc3545', '#6c757d',
          '#20c997', '#fd7e14', '#6610f2', '#e83e8c', '#17a2b8'
        ],
        borderWidth: 2,
      }
    ]
  };

  // Chart data for items by type
  const typeChartData = {
    labels: Object.keys(itemsByType),
    datasets: [
      {
        data: Object.values(itemsByType).map(type => type.totalQuantity),
        backgroundColor: [
          '#0d6efd', '#198754', '#ffc107', '#dc3545', '#6c757d',
          '#20c997', '#fd7e14', '#6610f2', '#e83e8c', '#17a2b8'
        ],
        borderWidth: 2,
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <div className="d-flex flex-column gap-4">
      <PageSection
        title={t('statistics.rentalOverview')}
        description={t('rentals.detail.statisticsOverviewDescription', 'Souhrn trvání, množství položek a celkové ceny této zápůjčky.')}
      >
        <div className="app-stat-grid">
          <StatsCard title={t('statistics.daysTotal')} value={durationInDays} icon="clock" colorClass="primary" />
          <StatsCard title={t('statistics.totalQuantity')} value={totalQuantity} icon="package" colorClass="success" />
          <StatsCard title={t('statistics.uniqueItems')} value={totalItems} icon="list-details" colorClass="warning" />
          <StatsCard title={t('statistics.totalPrice')} value={formatCurrency(totalPrice, { decimals: 0 })} icon="currency-dollar" colorClass="info" />
        </div>
      </PageSection>

      {(Object.keys(itemsByCategory).length > 0 || Object.keys(itemsByType).length > 0) ? (
        <Row className="g-3">
          {Object.keys(itemsByCategory).length > 0 && (
            <Col lg={6}>
              <PageSection title={t('statistics.itemsByCategory')}>
                <div style={{ height: '300px' }}>
                  <Doughnut data={categoryChartData} options={doughnutOptions} />
                </div>
              </PageSection>
            </Col>
          )}

          {Object.keys(itemsByType).length > 0 && (
            <Col lg={6}>
              <PageSection title={t('statistics.itemsByType')}>
                <div style={{ height: '300px' }}>
                  <Doughnut data={typeChartData} options={doughnutOptions} />
                </div>
              </PageSection>
            </Col>
          )}
        </Row>
      ) : (
        <PageSection>
          <EmptyState
            title={t('statistics.noDataTitle', 'Zatím nejsou k dispozici statistická data')}
            message={t('statistics.noDataMessage', 'Pro tuto zápůjčku zatím nejsou dostupná data po kategoriích ani typech položek.')}
          />
        </PageSection>
      )}

      <PageSection
        title={t('statistics.pricingBreakdown')}
        description={t('rentals.detail.statisticsPricingDescription', 'Porovnání ceny podle dne, položky a hodinového přepočtu pro rychlou kontrolu nacenění.')}
      >
        <div className="app-stat-grid">
          <StatsCard title={t('statistics.pricePerDay')} value={formatCurrency(averagePricePerDay, { decimals: 0 })} icon="calendar-time" colorClass="primary" />
          <StatsCard title={t('statistics.pricePerItem')} value={formatCurrency(averagePricePerItem, { decimals: 0 })} icon="calculator" colorClass="warning" />
          <StatsCard title={t('statistics.pricePerHour')} value={formatCurrency(totalPrice / (durationInHours || 1), { decimals: 0 })} icon="clock-hour-4" colorClass="info" />
        </div>
      </PageSection>
    </div>
  );
};

export default Statistics;
